import React from "react";
import { Box, Typography, Stack, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LocalGasStationRoundedIcon from "@mui/icons-material/LocalGasStationRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";

import { formatSignedUsd, formatSignedPercent, formatSignedPp } from "../../utils/formatters";
import { IBotData } from "../../shared/types";
import { useAuth } from "../../hooks/useAuth";
import { isWaitingForFunds } from "../../utils/botUtils";
import { bonusYieldUsd, capitalBasis, gasSeverity, returnPct } from "../../utils/botMetrics";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { StatusPill, StatusTone } from "../../components/ui/StatusPill";
import { StatusRing } from "../../components/ui/StatusRing";
import { MiniStat } from "../../components/ui/MiniStat";
import { Sparkline } from "../../components/ui/Sparkline";
import { ChainAvatar, chainName, chainOf, pairOf } from "../../components/ui/ChainAvatar";
import { tokens } from "../../theme";

interface SummaryBotCardProps {
  bot: IBotData;
  inRangeCount: number;
  totalBotsCount: number;
  /** Bots of the family with an open position whose price left the range. */
  outOfRangeCount?: number;
  /** LP index history of the family (sparkline). */
  trend?: number[];
  onDoubleClick?: (botId: string) => void;
  /** Anchor for "jump to" links (attention chips). */
  id?: string;
  /** Worst state of the family's bots, for the ring (same scale as the sidebar dot). */
  ringTone?: StatusTone;
  /** Any bot of the family is awaiting funds (the combined stats only carry one bot's steps). */
  awaitingFunds?: boolean;
}

/** Strategy (bot family) card on the overview; click opens the family. */
export const SummaryBotCard: React.FC<SummaryBotCardProps> = React.memo(
  ({ bot, inRangeCount, totalBotsCount, outOfRangeCount = 0, trend, onDoubleClick, id, ringTone, awaitingFunds }) => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    const { groups } = useAuth();
    const isAdmin = groups.includes("admin");
    const s = bot.stats;
    const family = s.botName.replace(/_\d+$/, "");
    const chain = chainOf(family);

    // Family LP − HODL in percentage points (Σ hodlDiffUSD / Σ basis), same figure as the family hero.
    const vsHodl = returnPct(s.hodlDiffUSD || 0, capitalBasis(s));
    const extraYield = bonusYieldUsd(s);

    const isBalancing = (s.balancingUSD || 0) + (s.lentUSD || 0) > 0;
    const isLowGas = isAdmin && s.isLowGas === true;
    const isWaiting = isAdmin && (awaitingFunds ?? isWaitingForFunds(s));
    const netColor = s.pnlUSD < 0 ? theme.palette.error.main : theme.palette.success.main;
    const hodlColor = vsHodl < 0 ? theme.palette.error.main : theme.palette.success.main;

    // Most severe first; two fit on the narrowest card, the rest fold into "+N".
    const flags: { key: string; tone: StatusTone; label: string; icon?: React.ReactNode }[] = [
      outOfRangeCount > 0 && {
        key: "range",
        tone: "error" as StatusTone,
        label: outOfRangeCount > 1 ? `${outOfRangeCount} out of range` : "Out of range",
      },
      isLowGas && {
        key: "gas",
        tone: gasSeverity(s) as StatusTone,
        label: "Low gas",
        icon: <LocalGasStationRoundedIcon sx={{ fontSize: 13 }} />,
      },
      isWaiting && {
        key: "funds",
        tone: "warning" as StatusTone,
        label: "Awaiting funds",
        icon: <HourglassBottomRoundedIcon sx={{ fontSize: 13 }} />,
      },
      isBalancing && {
        key: "balancing",
        tone: "brand" as StatusTone,
        label: "Balancing",
        icon: <SyncRoundedIcon sx={{ fontSize: 13 }} />,
      },
    ].filter(Boolean) as { key: string; tone: StatusTone; label: string; icon?: React.ReactNode }[];
    const severity: Record<StatusTone, number> = { error: 0, warning: 1, brand: 2, info: 3, success: 4, neutral: 5 };
    flags.sort((a, b) => severity[a.tone] - severity[b.tone]);
    const shownFlags = flags.length > 2 ? flags.slice(0, 1) : flags;
    const hiddenFlags = flags.slice(shownFlags.length);
    // Ring colour = the family's worst state, like the sidebar dot: red (out of range / no position)
    // > gold (balancing) > green (all in range). Derived from the counts when not given.
    const worstTone: StatusTone =
      ringTone ??
      (outOfRangeCount > 0
        ? "error"
        : inRangeCount >= totalBotsCount
        ? "success"
        : isBalancing
        ? "brand"
        : "error");
    const tint = (tokens.chainTint as Record<string, string>)[chain];

    const open = () => onDoubleClick && onDoubleClick(s.botName);

    return (
      <SurfaceCard
        id={id}
        interactive
        radius="lg"
        padding="none"
        tint={tint}
        role="link"
        tabIndex={0}
        aria-label={`Open ${family}`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          open();
        }}
        onDoubleClick={(e: React.MouseEvent) => e.stopPropagation()}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        sx={{ height: "100%", display: "flex", flexDirection: "column", scrollMarginTop: { xs: 168, md: 108 } }}
      >
        <Box sx={{ px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 } }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <ChainAvatar chain={chain} size={40} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{ fontSize: { xs: "1rem", md: "1.0625rem" }, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.3 }}
                noWrap
              >
                {pairOf(family)}
              </Typography>
              <Typography component="div" noWrap sx={{ fontSize: "0.78125rem", color: "text.secondary", mt: 0.25 }}>
                {chainName(chain)} · {totalBotsCount} bot{totalBotsCount === 1 ? "" : "s"}
              </Typography>
            </Box>
            <StatusRing value={inRangeCount} total={totalBotsCount} size={44} tone={worstTone} />
          </Stack>

          {/* Fixed slot for state flags so sparklines line up across a row */}
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              minHeight: { xs: 0, md: 22 },
              mt: { xs: flags.length > 0 ? 1.5 : 0, md: 1.5 },
              minWidth: 0,
            }}
          >
            {shownFlags.map((f) => (
              <StatusPill key={f.key} tone={f.tone} size="sm" label={f.label} icon={f.icon} />
            ))}
            {hiddenFlags.length > 0 && (
              <Tooltip title={hiddenFlags.map((f) => f.label).join(" · ")}>
                <span>
                  <StatusPill tone="neutral" size="sm" dot={false} label={`+${hiddenFlags.length}`} />
                </span>
              </Tooltip>
            )}
          </Stack>

          {/* Performance */}
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ mt: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="div"
                sx={{
                  fontSize: { xs: "1.625rem", md: "1.875rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  color: netColor,
                  whiteSpace: "nowrap",
                }}
              >
                {formatSignedPercent(s.pnlPerCapital)}
              </Typography>
              <Typography component="div" noWrap sx={{ fontSize: "0.78125rem", color: "text.secondary", mt: 0.5 }}>
                {formatSignedUsd(s.pnlUSD)} net
              </Typography>
            </Box>
            {isAdmin && (
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography component="div" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  vs HODL
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em", color: hodlColor, whiteSpace: "nowrap" }}
                  title={formatSignedUsd(s.hodlDiffUSD)}
                >
                  {formatSignedPp(vsHodl)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Trend — anchored to the footer so lines align across a row */}
        <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
          {trend && trend.length > 1 ? (
            <Sparkline data={trend} color={netColor} height={isPhone ? 40 : 54} fill dot />
          ) : (
            <Box sx={{ height: isPhone ? 40 : 54 }} />
          )}
        </Box>

        <Box sx={{ px: { xs: 2.5, md: 3 }, pt: { xs: 1.25, sm: 1.5 }, pb: { xs: 2.25, md: 3 }, mt: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: { xs: 1.5, sm: 1 },
              mt: { xs: 0, sm: 0.5 },
            }}
          >
            <MiniStat label="Capital" value={s.totalCapitalUSD} flat={isPhone} />
            <MiniStat label="Fees" value={s.totalPoolFeesUSDOrig} flat={isPhone} />
            <MiniStat label="Bonus yield" value={extraYield} flat={isPhone} />
          </Box>
        </Box>
      </SurfaceCard>
    );
  }
);
